import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Dropdown, Spin, Typography } from "antd";
import type { MenuProps } from "antd";
import { BellOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axiosInstance from "@/lib/util/axios";
import { API_URL } from "@/config";

interface AdminNotification {
  _id: string;
  title: string;
  message: string;
  link?: string | null;
  createdAt: string;
  isRead: boolean;
}

interface AdminNotificationFeed {
  unreadCount: number;
  notifications: AdminNotification[];
}

export interface AdminNotificationBellProps {
  pollIntervalMs?: number;
  limit?: number;
  className?: string;
}

export function AdminNotificationBell({
  pollIntervalMs = 45_000,
  limit = 10,
  className,
}: AdminNotificationBellProps) {
  const navigate = useNavigate();
  const [feed, setFeed] = useState<AdminNotificationFeed>({
    unreadCount: 0,
    notifications: [],
  });
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await axiosInstance.get<AdminNotificationFeed>(
        `${API_URL}/api/notifications/admin-feed`,
        { params: { limit } }
      );
      setFeed(data);
    } catch (error) {
      if (!silent) console.error("Could not load admin notifications", error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchNotifications();
    const interval = window.setInterval(() => fetchNotifications(true), pollIntervalMs);
    return () => window.clearInterval(interval);
  }, [fetchNotifications, pollIntervalMs]);

  const markAllRead = async () => {
    await axiosInstance.patch(`${API_URL}/api/notifications/admin-feed/read-all`);
    setFeed((current) => ({
      unreadCount: 0,
      notifications: current.notifications.map((notification) => ({
        ...notification,
        isRead: true,
      })),
    }));
  };

  const openNotification = async (notification: AdminNotification) => {
    if (!notification.isRead) {
      await axiosInstance.patch(
        `${API_URL}/api/notifications/admin-feed/${notification._id}/read`
      );
      setFeed((current) => ({
        unreadCount: Math.max(current.unreadCount - 1, 0),
        notifications: current.notifications.map((item) =>
          item._id === notification._id ? { ...item, isRead: true } : item
        ),
      }));
    }
    if (notification.link) navigate(notification.link);
  };

  const menuItems = useMemo<MenuProps["items"]>(() => {
    if (loading) {
      return [{ key: "loading", disabled: true, label: <Spin size="small" /> }];
    }

    const items: MenuProps["items"] = feed.notifications.length
      ? feed.notifications.map((notification) => ({
          key: notification._id,
          label: (
            <div className="w-80 max-w-[75vw] py-1">
              <div className="flex items-start gap-2">
                <span
                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                    notification.isRead ? "bg-transparent" : "bg-brand"
                  }`}
                />
                <div className="min-w-0">
                  <Typography.Text strong={!notification.isRead}>
                    {notification.title}
                  </Typography.Text>
                  <div className="whitespace-normal text-xs text-muted-foreground">
                    {notification.message}
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    {new Date(notification.createdAt).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          ),
        }))
      : [{ key: "empty", disabled: true, label: "No notifications" }];

    if (feed.unreadCount > 0) {
      items.push({ type: "divider" });
      items.push({ key: "mark-all-read", label: "Mark all as read" });
    }
    return items;
  }, [feed.notifications, feed.unreadCount, loading]);

  const handleMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "mark-all-read") {
      void markAllRead();
      return;
    }
    const notification = feed.notifications.find((item) => item._id === key);
    if (notification) void openNotification(notification);
  };

  return (
    <Dropdown
      trigger={["click"]}
      placement="bottomRight"
      menu={{ items: menuItems, onClick: handleMenuClick }}
      onOpenChange={(open) => open && void fetchNotifications(true)}
    >
      <Badge count={feed.unreadCount} size="small" overflowCount={99}>
        <button
          type="button"
          className={className || "notification-btn"}
          aria-label={`Notifications${feed.unreadCount ? `, ${feed.unreadCount} unread` : ""}`}
        >
          <BellOutlined />
        </button>
      </Badge>
    </Dropdown>
  );
}

export default AdminNotificationBell;
